import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHeader } from "../DataUpload/FileUploadAndProcessForm";
import { Grid } from "antd";
import { ArrowRight, Upload} from "lucide-react";

/* ---------------- DATA ---------------- */

const models = [
  {
    id: "ml8",
    title: "Random Sampling Model",
    subtitle: "The Random Sampling Engine uses stratified and weighted sampling rather than blind randomness.",
    description:
      "Data is first grouped into meaningful layers—such as value bands, risk levels, processes, time periods, and COSO control objectives. Samples are then drawn proportionately from each layer.This ensures every critical area is represented, not just the largest or most obvious items.The result is audit-defensible sampling that aligns with COSO principles while maintaining true randomness and eliminating human bias.",
  },
  {
    id: "benford",
    title: "Benford’s Law",
    subtitle: "The Benford Intelligence Scanner goes far beyond a basic digit check.",
    description:
      "It simultaneously evaluates multiple digit patterns—first digit, second digit, digit combinations, and frequency shifts—to understand whether numbers behave naturally or unnaturally.What is extremely complex and time-consuming to do in Excel is handled seamlessly by Xynex AI. The system blends advanced statistical logic with AI-driven pattern recognition to detect subtle distortions, engineered numbers, and hidden manipulation signals.The result is a forensic-grade numerical fingerprint of your data—revealing irregularities that are almost impossible to spot manually.",
  },
  {
    id: "relative",
    title: "Relative Size Factor",
    subtitle: "The Relative Size Factor Scanner looks at how big each value is compared to everything else in the dataset.",
    description:
      "Instead of checking numbers in isolation, it asks a smarter question:“Does this value make sense relative to the overall population?”Using advanced statistical scaling and AI logic, it highlights entries that are disproportionately large or small, even if they don’t break any obvious rule. These subtle imbalances are extremely hard to detect manually, especially in large datasets.The scanner creates a context-aware size fingerprint, helping you spot values that quietly dominate, distort, or deserve deeper attention.",
  },
  {
    id: "normality",
    title: "Normality Threshold Scanner",
    subtitle: "The Normality Threshold Scanner works like a smart filter that learns what is usual in your data and then points out what looks unusual.",
    description:
      "Behind the scenes, it uses an Adaptive Normality Threshold Model a mix of math and AI to understand normal patterns. Once it knows what “normal” looks like, anything that falls far outside that range is automatically flagged.You don’t need to set rules or limits.The system learns from your data itself and highlights only the entries that deserve a closer look so you focus on exceptions, not noise.",
  },
  {
    id: "neighbourhood",
    title: "Neighbourhood Outlier Detector",
    subtitle: "The Neighbourhood Outlier Detection Scanner checks whether a data point behaves differently from others that look similar to it.",
    description:
      "Instead of comparing everything to a global average, it forms small “neighbourhoods” of similar records based on multiple attributes. If one entry stands out sharply from its closest peers, it gets flagged—even if it appears normal at first glance.This peer-level comparison is nearly impossible to perform manually at scale.By combining distance-based analytics with AI intelligence, the scanner uncovers local anomalies—the kind that blend in globally but look suspicious when viewed in context.",
  },
  {
    id: "density",
    title: "Value Density Indicator",
    subtitle: "The Value Density Indicator looks at where values cluster and where they suddenly spike.",
    description:
      "Instead of viewing numbers one by one, it studies how value is distributed across the dataset and identifies dense pockets of unusually high or low impact.Using advanced statistical layering and AI logic, it highlights concentrations that quietly drive disproportionate value—patterns that are extremely difficult to detect manually, yet often carry the most insight.",
  },
  {
    id: "risk",
    title: "Risk Outcome Forecast Model",
    subtitle: "The Risk Outcome Forecast Model looks ahead instead of backward.",
    description:
      "It studies historical patterns, trends, and anomaly signals to estimate where issues are most likely to materialize and what their potential impact could be.By combining statistical forecasting with AI-driven pattern learning, the model converts current signals into forward-looking insight, helping teams anticipate outcomes early—before risks translate into real losses or surprises.",
  },
  {
    id: "ensemble",
    title: "Ensemble Model",
    subtitle: "The Ensemble Mode brings multiple analytical models together to think as one.",
    description:
      "Instead of relying on a single signal, it combines insights from different detectors—statistical, anomaly-based, and AI-driven—to form a stronger, more reliable conclusion.This layered intelligence reduces false alarms and blind spots, delivering a balanced, high-confidence view that no single model could achieve on its own.",
  },
];

/* ---------------- COMPONENT ---------------- */
const { useBreakpoint } = Grid;
export default function ModelExplanationGrid({ handleGetStarted }) {
  const [active, setActive] = useState(null);
  const screens = useBreakpoint();

  return (
    <>
      {/* GRID */}
      <PageHeader screens={screens} />
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 rounded-3xl">
        <div className="mx-auto max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {models.map((model) => (
            <motion.div
              key={model.id}
              layoutId={`card-${model.id}`}
              className="rounded-3xl bg-gradient-to-r from-pink-300 via-violet-400 to-purple-500 backdrop-blur p-6 flex flex-col justify-between shadow-lg"
              whileHover={{ y: -4 }}
            >
              {/* CONTENT */}
              <div>
                {/* {model?.icon && <div className="mb-4">{model?.icon}</div>} */}
                <motion.h3
                  layoutId={`title-${model.id}`}
                  className="text-xl font-bold text-gray-700 mb-2"
                >
                  {model?.title}
                </motion.h3>

                <p className="text-sm text-white/90 mb-4">{model?.subtitle}</p>
              </div>

              {/* EXPLORE CTA */}
              <button
                onClick={() => setActive(model)}
                className="mt-6 flex items-center bg-black/40 px-4 py-2 rounded-full justify-between text-sm font-semibold text-white group"
              >
                <span>Explore</span>
                <span
                  className="ml-2 transition-transform group-hover:translate-x-1"
                >
                  <ArrowRight />
                </span>
              </button>
            </motion.div>
          ))}
        </div>

        <button
          onClick={handleGetStarted}
          className="mt-8 px-10 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-pink-500 text-white font-semibold text-base hover:scale-105 flex items-center justify-center gap-2"
        >
          <Upload className="w-5 h-5" />
          Upload your file
        </button>
      </div>

      {/* EXPANDED CARD */}
      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* BACKDROP */}
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
              onClick={() => setActive(null)}
            />

            {/* CARD */}
            <motion.div
              layoutId={`card-${active.id}`}
              className="
                relative w-[90%] max-w-xl
                rounded-3xl
                bg-gradient-to-r from-pink-300 via-violet-400 to-purple-500
                p-8
              "
            >
              <motion.h2
                layoutId={`title-${active.id}`}
                className="text-xl font-bold text-gray-700 mb-2"
              >
                {active.title}
              </motion.h2>

              <motion.p
                className="text-white/90 leading-relaxed font-semibold"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
              >
                {active.description}
              </motion.p>

              <button
                onClick={() => setActive(null)}
                className="
                  absolute top-4 right-4
                  w-8 h-8 rounded-full
                  bg-black/20 text-white
                  flex items-center justify-center
                  hover:scale-110 transition
                "
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
